import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { BreadcrumbsJp } from '../../breadcrumbs/breadcrumbs';
import { SearchBar } from '../../shared/form-element/mui-search-bar';
import { MuiButton } from '../../shared/form-element';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTablePagination } from '../../shared/mui/mui-table-pagination';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { H2 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';
import { ICsvType } from '../../../types/record/csv';

const vt = viewTexts.pages.csvList;

const SearchBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
  display: inline-block;
`;

interface Props {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  page: number;
  onPageChange: (value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  numRows: number;
  csvListFiltered: IList<ICsvType>;
  downloadCsvOnClick: (csv: ICsvType) => void;
}

export const CsvListView: FC<Props> = memo(
  ({
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    numRows,
    csvListFiltered,
    downloadCsvOnClick
  }: Readonly<Props>) => {
    const csvsWithHandler = useMemo(
      () =>
        csvListFiltered.map(csv =>
          tuple(
            {
              id: csv.id,
              name: csv.name,
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              downloadCsvOnClick(csv);
            }
          )
        ),
      [csvListFiltered, downloadCsvOnClick]
    );

    return (
      <Root>
        <BreadcrumbsJp />

        <H2>{vt.title}</H2>

        <SearchBarWrapper>
          <SearchBar
            placeholder={vt.searchBarPlaceholder}
            value={searchString}
            onInput={onSearchStringChange}
          />
        </SearchBarWrapper>

        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{vt.header.name}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
            {csvsWithHandler.map(
                ([
                  csv,
                  downloadButtonOnClickHandler,
                ]) => (
                  <MuiTableRow key={csv.id}>
                    <TableCell>{csv.name}</TableCell>
                    <TableCell align='right'>
                      <ButtonWrapper>
                        <MuiButton
                          label={vt.buttons.download}
                          onClick={downloadButtonOnClickHandler}
                        />
                      </ButtonWrapper>
                    </TableCell>
                  </MuiTableRow>
                )
              )}
            </TableBody>
          </Table>
          <MuiTablePagination
            numRows={numRows}
            page={page}
            onChangePage={onPageChange}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        </MuiPaper>
      </Root>
    );
  }
);

CsvListView.displayName = 'CsvListView';
